import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';

const Container = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  color: white;
  font-weight: 500;
  margin-bottom: 0.75rem;
  font-size: 1rem;
`;

const SearchBox = styled.div`
  position: relative;
  margin-bottom: 0.75rem;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 1rem;

  &::placeholder {
    color: rgba(255, 255, 255, 0.6);
  }

  &:focus {
    outline: none;
    border-color: rgba(255, 255, 255, 0.6);
    background: rgba(255, 255, 255, 0.15);
  }
`;

const DropdownList = styled.div`
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.05);
  max-height: 300px;
  overflow-y: auto;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  z-index: 100;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 4px;

    &:hover {
      background: rgba(255, 255, 255, 0.3);
    }
  }
`;

const DropdownHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.08);
  gap: 0.5rem;

  button {
    padding: 0.4rem 0.75rem;
    font-size: 0.85rem;
    background: rgba(102, 126, 234, 0.2);
    color: #667eea;
    border: 1px solid #667eea;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      background: rgba(102, 126, 234, 0.3);
    }

    &:active {
      transform: scale(0.95);
    }
  }
`;

const DropdownItem = styled.label`
  display: flex;
  align-items: flex-start;
  padding: 0.75rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  cursor: pointer;
  transition: background 0.2s;
  color: white;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const Checkbox = styled.input`
  accent-color: #667eea;
  margin-right: 0.75rem;
  margin-top: 0.25rem;
  cursor: pointer;
  flex-shrink: 0;
`;

const CollegeInfo = styled.div`
  flex: 1;
`;

const CollegeName = styled.div`
  font-weight: 500;
  margin-bottom: 0.25rem;
`;

const CollegeDetails = styled.div`
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 0.25rem;
`;

const StudentCount = styled.div`
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.6);
  font-style: italic;
`;

const SelectedList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 1rem;
  min-height: 2rem;
`;

const SelectedTag = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(102, 126, 234, 0.2);
  border: 1px solid #667eea;
  color: white;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  font-size: 0.9rem;

  button {
    background: none;
    border: none;
    color: #667eea;
    cursor: pointer;
    font-size: 1.2rem;
    padding: 0;
    line-height: 1;
    transition: color 0.2s;

    &:hover {
      color: #fff;
    }
  }
`;

const SummaryText = styled.div`
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.8);
  margin-top: 1rem;
  padding: 0.75rem;
  background: rgba(102, 126, 234, 0.1);
  border-radius: 6px;
  border-left: 3px solid #667eea;
`;

const EmptyState = styled.div`
  padding: 1rem;
  text-align: center;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.9rem;
`;

const ErrorMessage = styled.div`
  color: #ff6b6b;
  font-size: 0.85rem;
  margin-top: 0.5rem;
  padding: 0.5rem;
  background: rgba(255, 107, 107, 0.1);
  border-radius: 4px;
  border-left: 3px solid #ff6b6b;
`;

/**
 * Reusable component for selecting colleges
 * Designed for extensibility to SMS, WhatsApp, Push Notifications, etc.
 * 
 * Props:
 * - mode: 'multi' (SELECTED_COLLEGES) or 'single' (SINGLE_COLLEGE)
 * - onSelection: Callback function when selection changes
 * - selectedIds: Array of currently selected college IDs
 * - disabled: Boolean to disable the selector
 * - error: Error message to display
 */
const CollegeRecipientSelector = ({ 
  mode = 'multi', 
  onSelection, 
  selectedIds = [],
  disabled = false,
  error = null
}) => {
  const [colleges, setColleges] = useState([]);
  const [filteredColleges, setFilteredColleges] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const searchBoxRef = useRef(null);
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

  // Fetch colleges on mount
  useEffect(() => {
    const fetchColleges = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('adminToken');
        const response = await fetch(`${API_BASE_URL}/api/v1/admin/bulk-email/colleges`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          setColleges(data.data || []);
          setFilteredColleges(data.data || []);
        }
      } catch (err) {
        console.error('Failed to fetch colleges:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchColleges();
  }, [API_BASE_URL]);

  // Handle search
  useEffect(() => {
    const filtered = colleges.filter(college => {
      const searchLower = searchTerm.toLowerCase();
      return (
        college.name.toLowerCase().includes(searchLower) ||
        (college.city && college.city.toLowerCase().includes(searchLower)) ||
        (college.state && college.state.toLowerCase().includes(searchLower))
      );
    });

    setFilteredColleges(filtered);
  }, [searchTerm, colleges]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchBoxRef.current && !searchBoxRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggleCollege = (collegeId) => {
    if (mode === 'single') {
      // Single selection mode
      const newSelection = selectedIds.includes(collegeId) ? [] : [collegeId];
      onSelection(newSelection);
    } else {
      // Multi selection mode
      let newSelection;
      if (selectedIds.includes(collegeId)) {
        newSelection = selectedIds.filter(id => id !== collegeId);
      } else {
        newSelection = [...selectedIds, collegeId];
      }
      onSelection(newSelection);
    }
  };

  const handleSelectAll = () => {
    if (mode === 'multi') {
      if (selectedIds.length === filteredColleges.length) {
        onSelection([]);
      } else {
        onSelection(filteredColleges.map(c => c.id));
      }
    }
  };

  const handleClearAll = () => {
    onSelection([]);
  };

  const handleRemoveTag = (collegeId, e) => {
    e.preventDefault();
    const newSelection = selectedIds.filter(id => id !== collegeId);
    onSelection(newSelection);
  };

  const getSelectedColleges = () => {
    return colleges.filter(c => selectedIds.includes(c.id));
  };

  const totalStudents = getSelectedColleges().reduce((sum, c) => sum + (c.student_count || 0), 0);

  const label = mode === 'single' ? 'Select College' : 'Select Colleges';

  return (
    <Container>
      <Label htmlFor="college-search">{label}</Label>
      
      <SearchBox ref={searchBoxRef}>
        <SearchInput
          id="college-search"
          type="text"
          placeholder="Search by college name, city, or state..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={() => setIsOpen(true)}
          disabled={disabled || loading}
          aria-label={label}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
        />

        {isOpen && (
          <DropdownList role="listbox">
            {mode === 'multi' && (
              <DropdownHeader>
                <div>
                  {filteredColleges.length} college{filteredColleges.length !== 1 ? 's' : ''}
                </div>
                <div style={{ display: 'flex', gap: '0.25rem' }}>
                  <button 
                    onClick={handleSelectAll}
                    type="button"
                    title="Select all colleges"
                    aria-label="Select all colleges"
                  >
                    {selectedIds.length === filteredColleges.length && filteredColleges.length > 0
                      ? 'Deselect All'
                      : 'Select All'}
                  </button>
                  {selectedIds.length > 0 && (
                    <button 
                      onClick={handleClearAll}
                      type="button"
                      title="Clear all selections"
                      aria-label="Clear all selections"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </DropdownHeader>
            )}

            {loading ? (
              <EmptyState>Loading colleges...</EmptyState>
            ) : filteredColleges.length === 0 ? (
              <EmptyState>No colleges found</EmptyState>
            ) : (
              filteredColleges.map((college) => (
                <DropdownItem
                  key={college.id}
                  role="option"
                  aria-selected={selectedIds.includes(college.id)}
                >
                  <Checkbox
                    type="checkbox"
                    checked={selectedIds.includes(college.id)}
                    onChange={() => handleToggleCollege(college.id)}
                    disabled={disabled}
                    aria-label={`Select ${college.name}`}
                  />
                  <CollegeInfo>
                    <CollegeName>{college.name}</CollegeName>
                    {(college.city || college.state) && (
                      <CollegeDetails>
                        {[college.city, college.state].filter(Boolean).join(', ')}
                      </CollegeDetails>
                    )}
                    <StudentCount>
                      {college.student_count} student{college.student_count !== 1 ? 's' : ''}
                    </StudentCount>
                  </CollegeInfo>
                </DropdownItem>
              ))
            )}
          </DropdownList>
        )}
      </SearchBox>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      {selectedIds.length > 0 && (
        <>
          <SelectedList>
            {getSelectedColleges().map((college) => (
              <SelectedTag key={college.id}>
                <span>{college.name} ({college.student_count})</span>
                <button
                  type="button"
                  onClick={(e) => handleRemoveTag(college.id, e)}
                  aria-label={`Remove ${college.name}`}
                >
                  ×
                </button>
              </SelectedTag>
            ))}
          </SelectedList>

          {mode === 'multi' && (
            <SummaryText>
              {selectedIds.length} college{selectedIds.length !== 1 ? 's' : ''} selected • {totalStudents} student{totalStudents !== 1 ? 's' : ''}
            </SummaryText>
          )}
        </>
      )}
    </Container>
  );
};

export default CollegeRecipientSelector;
